"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const accentBorders = ["border-t-[12px] border-t-[hsl(210_85%_54%)]", "border-t-[12px] border-t-[hsl(220_90%_48%)]", "border-t-[12px] border-t-[hsl(190_100%_56%)]"];
function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function ArchiveConsumer({
  searchTerm,
  category,
  sortOrder
}: {
  searchTerm: string;
  category: string;
  sortOrder: string;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    currentPage,
    totalPages,
    setPage,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts() as any;
  const allCategories = Array.from(new Map((posts || []).flatMap((p: any) => p.categories || []).map((c: any) => [c.slug, c])).values());
  let filteredPosts = (posts || []).slice();
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filteredPosts = filteredPosts.filter((p: any) => (p.title || "").toLowerCase().includes(term) || stripHtml(p.excerpt || "").toLowerCase().includes(term));
  }
  if (category !== "all") {
    filteredPosts = filteredPosts.filter((p: any) => (p.categories || []).some((c: any) => c.slug === category));
  }
  filteredPosts.sort((a: any, b: any) => {
    const ad = a.date ? new Date(a.date).getTime() : 0;
    const bd = b.date ? new Date(b.date).getTime() : 0;
    return sortOrder === "oldest" ? ad - bd : bd - ad;
  });
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <div data-index={i} key={i} className="h-[460px] bg-card border-[3px] border-foreground animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="border-[3px] border-foreground bg-card p-8 text-center">
        <p className="font-default text-base text-foreground mb-4">
          Gagal memuat artikel.
        </p>
        <Button onClick={refetch} className="bg-blue-600 text-white border-[3px] border-foreground rounded-lg px-6 py-3 font-default uppercase tracking-wider hover:bg-blue-700">
          Coba Lagi
        </Button>
      </div>;
  }
  if (!filteredPosts || filteredPosts.length === 0) {
    return <div className="border-[3px] border-foreground bg-card p-12 text-center">
        <p className="font-mono text-sm uppercase tracking-[0.18em] text-muted-foreground">
          § TIDAK ADA ARTIKEL DITEMUKAN
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPosts.map((post: any, i: number) => {
        const accent = accentBorders[i % accentBorders.length];
        const primaryCategory = post.categories && post.categories.length > 0 ? post.categories[0] : null;
        const excerpt = stripHtml(post.excerpt || "").slice(0, 180);
        return <article data-index={i} key={post.id} className={`group bg-card ${accent} border-x-[3px] border-b-[3px] border-foreground flex flex-col transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_var(--foreground)]`}>
              <div className="relative aspect-[16/10] overflow-hidden border-b-[3px] border-foreground bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{
              background: "repeating-radial-gradient(circle, var(--foreground) 0 1.5px, transparent 1.5px 6px)",
              opacity: 0.15
            }} />}
                <div className="absolute inset-0 transition-opacity duration-300 pointer-events-none group-hover:opacity-[0.15]" style={{
              background: "repeating-radial-gradient(circle, var(--foreground) 0 1.5px, transparent 1.5px 6px)",
              opacity: 0.3
            }} />
              </div>

              <div className="p-6 flex flex-col flex-1 gap-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {primaryCategory ? <span className="inline-flex items-center border-[2px] border-foreground bg-card px-2.5 py-1 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-foreground">
                      § {primaryCategory.name}
                    </span> : <span className="inline-flex items-center border-[2px] border-foreground bg-card px-2.5 py-1 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-foreground">
                      § BERITA
                    </span>}
                  <span className="font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
                    {formatDate(post.date)}
                  </span>
                </div>

                <h3 className="font-default font-bold text-foreground leading-[1.15]" style={{
              fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)"
            }}>
                  {post.title}
                </h3>

                {excerpt && <p className="font-default text-foreground/80 leading-[1.55] flex-1" style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
                    {excerpt}
                  </p>}

                <div className="flex justify-end pt-2">
                  <Link to={post.link || "#"}>
                    <Button className="bg-blue-600 text-white border-[3px] border-foreground rounded-lg px-5 py-2.5 font-default text-xs uppercase tracking-[0.04em] hover:bg-blue-700 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[4px_4px_0_0_var(--foreground)] transition-all duration-200">
                      Baca Selengkapnya
                      <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2.5} />
                    </Button>
                  </Link>
                </div>
              </div>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="mt-16 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2 flex-wrap">
              <PaginationItem>
                <Button onClick={prevPage} disabled={!hasPrev} className="bg-card text-foreground border-[3px] border-foreground rounded-lg px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] hover:bg-foreground hover:text-background disabled:opacity-40 transition-colors">
                  ← SEBELUMNYA
                </Button>
              </PaginationItem>

              {Array.from({
            length: totalPages
          }, (_, idx) => idx + 1).map((pageNum, i) => <PaginationItem data-index={i} key={pageNum}>
                    <Button onClick={() => setPage(pageNum)} className={pageNum === currentPage ? "bg-blue-600 text-white border-[3px] border-foreground rounded-lg w-11 h-11 p-0 font-mono text-sm font-medium" : "bg-card text-foreground border-[3px] border-foreground rounded-lg w-11 h-11 p-0 font-mono text-sm font-medium hover:bg-foreground hover:text-background transition-colors"}>
                      {String(pageNum).padStart(2, "0")}
                    </Button>
                  </PaginationItem>)}

              <PaginationItem>
                <Button onClick={nextPage} disabled={!hasNext} className="bg-card text-foreground border-[3px] border-foreground rounded-lg px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] hover:bg-foreground hover:text-background disabled:opacity-40 transition-colors">
                  SELANJUTNYA →
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function Sidebar() {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: sortOrder === "oldest" ? "asc" : "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1645" id="sidebar" className="bg-background text-foreground relative border-t-[4px] border-b-[4px] border-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "repeating-radial-gradient(circle, var(--foreground) 0 1.5px, transparent 1.5px 8px)",
      opacity: 0.04
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-16 lg:mb-24">
          {}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-6 h-[3px] bg-foreground" />
              <span className="font-mono text-xs sm:text-sm uppercase tracking-[0.18em] text-foreground font-medium">
                § 01 — ARSIP BERITA
              </span>
            </div>
            <h2 className="font-default font-black text-foreground leading-[0.92]" style={{
            fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
            letterSpacing: "-0.035em"
          }}>
              <span className="block">PUSAT</span>
              <span className="block">INFORMASI</span>
              <span className="inline-flex items-center flex-wrap">
                <span className="relative inline-block">
                  <span className="absolute inset-0 -m-2 -z-0" aria-hidden="true">
                    <svg viewBox="0 0 200 100" className="w-full h-full" preserveAspectRatio="none">
                      <polygon points="100,5 115,25 140,15 135,40 165,40 145,60 165,85 135,75 140,95 115,80 100,98 85,80 60,95 65,75 35,85 55,60 35,40 65,40 60,15 85,25" fill="hsl(210 75% 60%)" stroke="var(--foreground)" strokeWidth="3" />
                    </svg>
                  </span>
                  <span className="relative z-10 px-6 py-2 text-white">
                    TERKINI.
                  </span>
                </span>
              </span>
            </h2>
            <p className="mt-8 font-default text-base sm:text-lg text-foreground/80 max-w-2xl leading-[1.55]">
              Temukan berita terbaru, klarifikasi, dan informasi terkini seputar kegiatan organisasi kami.
            </p>
          </div>

          {}
          <div className="lg:col-span-4 flex flex-col gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                CARI:
              </label>
              <Input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="TELUSURI ARSIP..." className="h-14 bg-card border-[3px] border-foreground rounded-lg px-4 font-default text-base text-foreground placeholder:text-muted-foreground placeholder:uppercase placeholder:tracking-wider focus-visible:ring-0 focus-visible:border-foreground focus-visible:outline-[3px] focus-visible:outline-blue-600 focus-visible:outline-offset-[3px]" />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                KATEGORI:
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-14 bg-card border-[3px] border-foreground rounded-lg px-4 font-mono text-sm uppercase tracking-[0.12em] text-foreground focus:ring-0 focus:border-foreground">
                  <SelectValue placeholder="SEMUA" />
                </SelectTrigger>
                <SelectContent className="bg-card border-[3px] border-foreground rounded-lg">
                  <SelectItem value="all" className="font-mono text-sm uppercase tracking-[0.12em]">
                    SEMUA KATEGORI
                  </SelectItem>
                  <SelectItem value="berita" className="font-mono text-sm uppercase tracking-[0.12em]">
                    BERITA
                  </SelectItem>
                  <SelectItem value="klarifikasi" className="font-mono text-sm uppercase tracking-[0.12em]">
                    KLARIFIKASI
                  </SelectItem>
                  <SelectItem value="opini" className="font-mono text-sm uppercase tracking-[0.12em]">
                    OPINI
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.18em] text-foreground mb-2 font-medium">
                URUTKAN:
              </label>
              <div className="grid grid-cols-2 gap-0 border-[3px] border-foreground rounded-lg overflow-hidden">
                <Button onClick={() => setSortOrder("newest")} className={sortOrder === "newest" ? "bg-blue-600 text-white rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-blue-700 border-r-[3px] border-foreground" : "bg-card text-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted border-r-[3px] border-foreground"}>
                  TERBARU
                </Button>
                <Button onClick={() => setSortOrder("oldest")} className={sortOrder === "oldest" ? "bg-blue-600 text-white rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-blue-700" : "bg-card text-foreground rounded-none h-12 font-mono text-xs uppercase tracking-[0.12em] hover:bg-muted"}>
                  TERLAMA
                </Button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="w-full h-[2px] mb-12" style={{
        background: "repeating-linear-gradient(to right, var(--foreground) 0 6px, transparent 6px 12px)"
      }} aria-hidden="true" />

        {}
        <PostsProvider wp_query={wp_query}>
          <ArchiveConsumer searchTerm={searchTerm} category={category} sortOrder={sortOrder} />
        </PostsProvider>
      </div>
    </section>;
}